import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarX2, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/i18n";
import { api } from "@/lib/api";

export default function Disdica() {
  const { lang, t } = useLanguage();
  const c = t.cancel;
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get(`/booking/cancel/${token}`)
      .then((res) => setBooking(res.data))
      .catch(() => setBooking(null))
      .finally(() => setLoading(false));
  }, [token]);

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/booking/cancel/${token}`);
      setDone(true);
    } catch (err) {
      setError(c.error);
    } finally {
      setBusy(false);
    }
  };

  const locale = lang === "it" ? "it-IT" : "en-GB";
  const dateStr = booking
    ? new Date(`${booking.date}T00:00:00`).toLocaleDateString(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  return (
    <div data-testid="disdica-page" className="min-h-screen pt-28 sm:pt-36 pb-20">
      <div className="container-editorial max-w-md">
        {loading && <p className="text-sm font-light text-slate-500">{t.articles.loading}</p>}

        {!loading && done && (
          <div className="text-center border border-slate-200 bg-white p-10" data-testid="disdica-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-brand" />
            <h1 className="mt-5 font-serif text-3xl font-light text-slate-900">{c.done}</h1>
            <p className="mt-3 text-sm font-light leading-relaxed text-slate-600">{c.cancelled}</p>
            <Link
              to="/"
              data-testid="disdica-back-home"
              className="mt-8 inline-flex rounded-full bg-brand px-7 py-3 text-sm font-medium text-white hover:bg-brand-dark transition-colors duration-300"
            >
              {c.backHome}
            </Link>
          </div>
        )}

        {!loading && !done && booking && (
          <div className="border border-slate-200 bg-white p-8 sm:p-10 text-center">
            <CalendarX2 className="h-10 w-10 mx-auto text-brand" />
            <h1 className="mt-4 font-serif text-3xl font-light text-slate-900">{c.title}</h1>
            <div className="mt-6 bg-brand-linen border border-slate-200 p-5 text-left" data-testid="disdica-summary">
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{c.summary}</p>
              <p className="mt-2 font-serif text-xl text-slate-900 capitalize">{dateStr}</p>
              <p className="mt-1 text-sm text-slate-600">
                {c.time}: {booking.time}
              </p>
              <p className="text-sm text-slate-600">
                {c.name}: {booking.name}
              </p>
            </div>
            {error && (
              <p className="mt-4 text-sm text-red-600" data-testid="disdica-error">
                {error}
              </p>
            )}
            <button
              data-testid="disdica-confirm-button"
              onClick={cancel}
              disabled={busy}
              className="mt-7 w-full rounded-full bg-brand px-7 py-3.5 text-sm font-medium text-white hover:bg-brand-dark transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? c.cancelling : c.confirm}
            </button>
            <Link to="/contatti" data-testid="disdica-keep-link" className="mt-4 inline-block text-sm font-light text-slate-500 link-underline">
              {c.keep}
            </Link>
          </div>
        )}

        {!loading && !done && !booking && (
          <div className="text-center border border-slate-200 bg-white p-10" data-testid="disdica-invalid">
            <h1 className="font-serif text-3xl font-light text-slate-900">{c.invalid}</h1>
            <Link
              to="/"
              data-testid="disdica-back-home"
              className="mt-8 inline-flex rounded-full bg-brand px-7 py-3 text-sm font-medium text-white hover:bg-brand-dark transition-colors duration-300"
            >
              {c.backHome}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
